import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";
import { useAuth } from "@/hooks/useAuth";

interface OperatorStats {
  operatorId: string | null;
  operatorName: string | null;
  docsProcessed: number;
  avgDurationMs: number;
  totalFields: number;
  totalCorrections: number;
  totalOverrides: number;
  correctionRate: number;
  overrideRate: number;
}

interface OperatorPerformanceResponse {
  operators: OperatorStats[];
}

interface MyActivityData {
  docsToday: number;
  avgDurationMs: number;
}

export function useMyActivity() {
  const { user } = useAuth();
  const [activity, setActivity] = useState<MyActivityData | null>(null);
  // R-182 — load-once (no poll), but a manual refresh must NOT blank the card.
  // `isLoading` is one-way; `isRefreshing` tracks a manual refetch so the card
  // gates its skeleton on initial load only.
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;

    setIsRefreshing(true);
    try {
      setError(null);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const from = today.toISOString();

      const data = await api.get<OperatorPerformanceResponse>(
        `/analytics/operator-performance?from=${encodeURIComponent(from)}`
      );

      // Find this user's stats
      const myStats = data.operators.find((op) => op.operatorId === user.userId);

      setActivity({
        docsToday: myStats?.docsProcessed ?? 0,
        avgDurationMs: myStats?.avgDurationMs ?? 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load activity");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  return { activity, isLoading, isRefreshing, error, refresh: load };
}
